import type { Language } from "@/constants";

/**
 * Currency and number formatting utilities with language support
 */

export interface CurrencyFormatOptions {
	/** Currency symbol or unit */
	currency?: string;
	/** Number of decimal places */
	decimals?: number;
	/** Use thousands separator */
	useSeparator?: boolean;
	/** Convert to thousands (k) or millions (M) */
	compact?: "k" | "M" | false;
	/** Language for locale formatting */
	language?: Language;
}

/**
 * Format a number with locale-aware formatting using Intl.NumberFormat class
 * @param value - Number to format
 * @param language - Language for locale formatting
 * @returns Formatted number string
 */
export function formatNumber(
	value: number | string,
	language: Language = "fa",
): string {
	const numValue = typeof value === "string" ? Number.parseFloat(value) : value;

	if (Number.isNaN(numValue)) return "0";

	// Using Intl.NumberFormat class for internationalization
	const numberFormatter = new Intl.NumberFormat(language);
	return numberFormatter.format(numValue);
}

/**
 * Format currency with proper locale formatting
 * @param value - Amount to format
 * @param options - Formatting options
 * @returns Formatted currency string
 */
export function formatCurrency(
	value: number | string,
	options: CurrencyFormatOptions = {},
): string {
	const {
		currency = "",
		decimals = 0,
		useSeparator = true,
		compact = false,
		language = "fa",
	} = options;

	const numValue = typeof value === "string" ? Number.parseFloat(value) : value;

	if (Number.isNaN(numValue)) return `0${currency}`;

	let formattedValue = numValue;
	let suffix = "";

	// Handle compact formatting
	if (compact === "k") {
		formattedValue = numValue / 1000;
		if (language === "fa") {
			suffix = " کا";
		} else {
			suffix = " k";
		}
	} else if (compact === "M") {
		formattedValue = numValue / 1000000;
		suffix = "M";
	}

	// Format with locale using Intl.NumberFormat class
	let result: string;
	if (useSeparator) {
		const currencyFormatter = new Intl.NumberFormat(language, {
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		});
		result = currencyFormatter.format(formattedValue);
	} else {
		result = formattedValue.toFixed(decimals);
	}

	return `${result}${suffix}${currency}`;
}

/**
 * Format price for table display (with k suffix)
 * @param value - Price value
 * @param language - Language for formatting
 * @returns Formatted price string
 */
export function formatPrice(
	value: number | string,
	language: Language = "fa",
): string {
	return formatCurrency(value, {
		compact: "k",
		decimals: 0,
		language,
	});
}

/**
 * Format large numbers (millions)
 * @param value - Number value
 * @param language - Language for formatting
 * @returns Formatted number string
 */
export function formatLargeNumber(
	value: number | string,
	language: Language = "fa",
): string {
	return formatCurrency(value, {
		compact: "M",
		decimals: 1,
		language,
	});
}

/**
 * Coin prices are quoted per 100k coins, so a sale's total value is
 * `unit_price * transferred_value / 100_000`.
 */
export const COIN_PRICE_UNIT = 100_000;

/**
 * Compute the total price of a coin sale from the per-100k unit price and the
 * transferred coin amount.
 * @param unitPrice - Price per 100k coins (toman)
 * @param transferredValue - Transferred coin amount (number or numeric string)
 * @returns Total sale price in toman
 */
export function calcCoinSaleTotalPrice(
	unitPrice: number,
	transferredValue: number | string,
): number {
	const transferred =
		typeof transferredValue === "string"
			? Number.parseFloat(transferredValue)
			: transferredValue;

	if (Number.isNaN(transferred) || Number.isNaN(unitPrice)) return 0;

	return Math.floor((unitPrice * transferred) / COIN_PRICE_UNIT);
}

/**
 * Round a number DOWN to the nearest base (e.g. 1000, 100, 10)
 * @param value - Number to round
 * @param base - Base to round down to (default: 1000)
 * @returns Rounded-down number
 */
export function roundDown(value: number | string, base = 1000): number {
	const numValue = typeof value === "string" ? Number.parseFloat(value) : value;

	if (Number.isNaN(numValue)) return 0;
	if (base <= 0 || Number.isNaN(base)) return 0;

	return Math.floor(numValue / base) * base;
}
